// ─── A lazy chunk that fails to load is retried before it becomes a crash ─────
// PURE (no React, no DOM) so node can test it; lazyRetry.js wraps it for React.
//
// "Importing a module script failed" (Safari) / "Failed to fetch dynamically
// imported module" (Chrome, Firefox) is a NETWORK failure on one chunk: a 5G
// hand-off on a phone, a Render restart mid-deploy, a proxy hiccup. Every page in
// Pulse is a lazy chunk, so one dropped request used to surface as the whole-app
// crash screen (reported 2026-09-20, on a phone). The error boundary's answer was
// one reload per session — and the reloaded app lazy-loads its landing route in
// the first few milliseconds, so a blip lasting longer than that hit the same
// wall again and showed the crash.
//
// Here: the import itself is retried with a short backoff, a few times, before
// anything is thrown. Only chunk-load failures are retried — a module that throws
// while evaluating is a real bug and must surface at once, unchanged.

const CHUNK_ERROR = /(importing a module script failed|dynamically imported module|Failed to fetch|Load failed|ChunkLoadError|error loading dynamically imported module|NetworkError|Unable to preload CSS)/i;

export const isChunkLoadError = (e) => CHUNK_ERROR.test(String(e?.message || e || ''));

// Try `factory()` (the `() => import('./Page.jsx')`) up to `tries` times, waiting
// `delay` then doubling between attempts. `sleep` is injectable for tests.
export async function retryImport(factory, { tries = 4, delay = 600, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  let wait = delay;
  for (let attempt = 1; ; attempt++) {
    try {
      return await factory();
    } catch (e) {
      if (attempt >= tries || !isChunkLoadError(e)) throw e;
      await sleep(wait);
      wait *= 2;
    }
  }
}
